import { addScanFromScanner, loadSavedScans, sendMessage } from "actions";
import {
  DriverEventType,
  type DriverEffect,
} from "lib/deviceDrivers/sessionFramework/adapter/driverEffects";
import {
  type Xrs2iEvent,
  Xrs2iEventType,
  type Xrs2iScanEvent,
  type Xrs2iErrorEvent,
  type Xrs2iSavedScansProgressEvent,
  type Xrs2iSavedScansCompleteEvent,
} from "lib/deviceDrivers/truTest/xrs2i/device/types";

export type DriverAction =
  | ReturnType<typeof addScanFromScanner>
  | ReturnType<typeof sendMessage>
  | ReturnType<typeof loadSavedScans>;

export type DriverActionResult = DriverAction[] | undefined;

/**
 * Maps one scanner event into the Redux actions or notifications the app should see.
 * This keeps the protocol layer focused on device state while the adapter owns store-facing work.
 */
export function mapDeviceEventToDriverEffects(
  deviceId: string,
  event: Xrs2iEvent,
): DriverEffect<DriverAction>[] {
  switch (event.type) {
    case Xrs2iEventType.SCAN:
      return mapScanEventToEffects(deviceId, event);

    case Xrs2iEventType.ERROR:
      return mapErrorEventToEffects(event);

    case Xrs2iEventType.INVALID_EID:
      return mapInvalidEidEventToEffects();

    case Xrs2iEventType.SAVED_SCANS_PROGRESS:
      return mapSavedScansProgressEventToEffects(deviceId, event);

    case Xrs2iEventType.SAVED_SCANS_COMPLETE:
      return mapSavedScansCompleteEventToEffects(deviceId, event);

    default:
      return assertNever(event);
  }
}

function assertNever(value: never): never {
  throw new Error(`Unhandled XRS2i event: ${JSON.stringify(value)}`);
}

function mapScanEventToEffects(
  deviceId: string,
  event: Xrs2iScanEvent,
): DriverEffect<DriverAction>[] {
  return [
    {
      type: DriverEventType.ACTION,
      action: addScanFromScanner(deviceId, {
        created: event.created,
        EID: event.eid,
        draftName: "",
      }),
    },
  ];
}

function mapErrorEventToEffects(
  event: Xrs2iErrorEvent,
): DriverEffect<DriverAction>[] {
  return [
    {
      type: DriverEventType.INTERNAL_ERROR,
      internalError: `Error communicating with XRP2i (${event.code})`,
    },
  ];
}

function mapInvalidEidEventToEffects(): DriverEffect<DriverAction>[] {
  return [
    {
      type: DriverEventType.NOTIFY,
      // Tell the operator that the scanner returned data we could not normalise into an EID.
      message: "Received invalid EID data from scanner",
    },
  ];
}

function mapSavedScansProgressEventToEffects(
  deviceId: string,
  event: Xrs2iSavedScansProgressEvent,
): DriverEffect<DriverAction>[] {
  return [
    {
      type: DriverEventType.ACTION,
      // Keep partial session results in Redux as each session finishes so progress screens can update live.
      action: loadSavedScans(deviceId, true, event.importedScans, {
        append: true,
        isFinal: false,
        completedSessions: event.completedSessions,
        totalSessions: event.totalSessions,
      }),
    },
  ];
}

function mapSavedScansCompleteEventToEffects(
  deviceId: string,
  event: Xrs2iSavedScansCompleteEvent,
): DriverEffect<DriverAction>[] {
  return [
    {
      type: DriverEventType.ACTION,
      // Mark the import as final once the scanner reports that every saved session has been read.
      action: loadSavedScans(deviceId, event.success, event.importedScans, {
        append: false,
        isFinal: true,
        completedSessions: event.completedSessions,
        totalSessions: event.totalSessions,
      }),
    },
  ];
}
